import React, { useEffect, useState } from 'react';
import * as Scrivito from 'scrivito';
import { Helmet, HelmetProvider } from 'react-helmet-async';

// Import page components – side-effects register obj classes + components
import HomepageComponent from './Objs/Homepage';
import PageComponent from './Objs/Page';
import RootPageComponent from './Objs/RootPage';
import LoginPage from './components/LoginPage';
import UniversalTranslatorButton from './components/UniversalTranslatorButton';

const PAGE_COMPONENTS = {
  Homepage: HomepageComponent,
  Page: PageComponent,
  RootPage: RootPageComponent,
};

// Read by src/entry-server.jsx after ReactDOMServer.renderToString() to pull
// out the <head>/<html>/<body> tags Helmet collected during that render -
// react-helmet-async's documented SSR pattern (it can't reach into a real DOM
// on the server, so it needs somewhere else to deposit the result).
export const helmetContext = {};

// Scrivito.currentPage() can briefly resolve to null right after a fresh page
// load – its permalink index is still loading – before returning the real
// page a moment later. Rendering that null immediately caused a real,
// reproducible 404 flash on every hard navigation (confirmed by polling the
// DOM through the first ~2s of a fresh load). Waiting this long for it to
// settle before trusting a null as a genuine not-found avoids that; a truly
// missing page just takes this much longer to show its 404.
const NOT_FOUND_GRACE_MS = 800;

const AppContent = Scrivito.connect(() => {
  const currentPage = Scrivito.currentPage();
  const [notFoundConfirmed, setNotFoundConfirmed] = useState(false);

  useEffect(() => {
    if (currentPage !== null) {
      setNotFoundConfirmed(false);
      return undefined;
    }
    const timer = setTimeout(() => setNotFoundConfirmed(true), NOT_FOUND_GRACE_MS);
    return () => clearTimeout(timer);
  }, [currentPage]);

  if (currentPage === undefined || (currentPage === null && !notFoundConfirmed)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (currentPage === null) {
    // The app is served as a static SPA shell, so this route still answers
    // HTTP 200 – noindex is the only way to tell crawlers it isn't real
    // content and stop them ranking/indexing it as a soft 404.
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="text-center">
            <h1 className="display-1">404</h1>
            <p className="text-muted">Page not found</p>
            <a href="/" className="btn btn-primary">Go Home</a>
          </div>
        </div>
      </>
    );
  }

  const Component = PAGE_COMPONENTS[currentPage.objClass()];

  if (!Component) {
    return (
      <div className="container py-5">
        <p className="text-muted">No component for page type: {currentPage.objClass()}</p>
      </div>
    );
  }

  return <Component page={currentPage} />;
});

export default function App() {
  // Never true during SSR (src/entry-server.jsx renders a Scrivito Obj, never
  // the /login route), but window doesn't exist there, so this must not run
  // unguarded or every prerendered page would crash the build.
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return (
      <HelmetProvider context={helmetContext}>
        <LoginPage />
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider context={helmetContext}>
      <AppContent />
      <UniversalTranslatorButton />
    </HelmetProvider>
  );
}
